import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Textarea,
  buttonStyles,
  type Tone,
} from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import { SupportReplyDraft } from './ai/drafts';
import type {
  FormSubmissionList,
  SupportAgent,
  SupportMacro,
  SupportReport,
  SupportTicket,
  SupportTicketList,
  SupportTicketReply,
  SupportTicketUpdated,
} from '../lib/types';

/**
 * Satış → Destek talepleri (F6-15, ADR-096): talep listesi (durum, atanan, öncelik, arama), talep ayrıntısı
 * (yazışma, müşteriye yanıt — e-postayla da gider —, iç not, hazır yanıt, durum/öncelik/atama), hazır yanıtlar ve
 * destek–memnuniyet raporu (iade nedenleri dahil). İletişim mesajını talebe dönüştürme penceresi de buradadır.
 */

const ROUTE = '/destek-talepleri';
const STATUSES = ['open', 'waiting', 'resolved', 'closed'] as const;
const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
const CATEGORIES = ['order', 'shipping', 'return', 'payment', 'product', 'account', 'other'] as const;
type TicketStatus = (typeof STATUSES)[number];
type Priority = (typeof PRIORITIES)[number];
type Category = (typeof CATEGORIES)[number];

const statusTone: Record<TicketStatus, Tone> = {
  open: 'amber',
  waiting: 'slate',
  resolved: 'moss',
  closed: 'neutral',
};
const priorityTone: Record<Priority, Tone> = {
  low: 'neutral',
  normal: 'slate',
  high: 'amber',
  urgent: 'rose',
};

function useDuration() {
  const t = useT();
  return (minutes: number | null) => {
    if (minutes === null) return '—';
    if (minutes >= 1440)
      return t('admin.support.durationDays', {
        d: Math.floor(minutes / 1440),
        h: Math.floor((minutes % 1440) / 60),
      });
    if (minutes >= 60)
      return t('admin.support.durationHours', { h: Math.floor(minutes / 60), m: minutes % 60 });
    return t('admin.support.durationMinutes', { m: minutes });
  };
}

// ---------------------------------------------------------------------------- Liste

export function SupportTicketsPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const [status, setStatus] = useState<'active' | 'all' | TicketStatus>('active');
  const [assigned, setAssigned] = useState('');
  const [priority, setPriority] = useState<'' | Priority>('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const params = new URLSearchParams({ status, page: String(page) });
  if (assigned) params.set('assigned', assigned);
  if (priority) params.set('priority', priority);
  if (q.trim()) params.set('q', q.trim());
  const list = useQuery({
    queryKey: ['support-tickets', params.toString()],
    queryFn: () => api.get<SupportTicketList>(`/v1/admin/support/tickets?${params.toString()}`),
  });
  const agents = useQuery({
    queryKey: ['support-agents'],
    queryFn: () => api.get<SupportAgent[]>('/v1/admin/support/agents'),
  });
  const d = list.data;
  const pages = d ? Math.max(1, Math.ceil(d.total / d.pageSize)) : 1;
  const reset = () => setPage(1);
  return (
    <PageFrame helpKey="page.support_tickets">
      <p className="max-w-[70ch] text-sm text-ink-2">{t('admin.support.lead')}</p>
      {d && (
        <div className="flex flex-wrap gap-2" aria-label={t('admin.support.countsLabel')}>
          <Badge tone="amber">{t('admin.support.countOpen', { count: d.counts.open })}</Badge>
          <Badge tone="slate">{t('admin.support.countWaiting', { count: d.counts.waiting })}</Badge>
          <Badge tone={d.counts.unassigned ? 'rose' : 'neutral'}>
            {t('admin.support.countUnassigned', { count: d.counts.unassigned })}
          </Badge>
        </div>
      )}
      <Section title={t('admin.support.listTitle')}>
        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t('admin.support.filterStatus')} help={<HelpTip k="support.filter_status" />}>
            {(p) => (
              <Select
                {...p}
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value as typeof status);
                  reset();
                }}
              >
                <option value="active">{t('admin.support.filterActive')}</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`admin.support.statuses.${s}`)}
                  </option>
                ))}
                <option value="all">{t('common.all')}</option>
              </Select>
            )}
          </Field>
          <Field label={t('admin.support.filterAssigned')} help={<HelpTip k="support.filter_assigned" />}>
            {(p) => (
              <Select
                {...p}
                value={assigned}
                onChange={(e) => {
                  setAssigned(e.target.value);
                  reset();
                }}
              >
                <option value="">{t('common.all')}</option>
                <option value="me">{t('admin.support.assignedMe')}</option>
                <option value="unassigned">{t('admin.support.unassigned')}</option>
                {(agents.data ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.support.filterPriority')} help={<HelpTip k="support.filter_priority" />}>
            {(p) => (
              <Select
                {...p}
                value={priority}
                onChange={(e) => {
                  setPriority(e.target.value as typeof priority);
                  reset();
                }}
              >
                <option value="">{t('common.all')}</option>
                {PRIORITIES.map((x) => (
                  <option key={x} value={x}>
                    {t(`admin.support.priorities.${x}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.support.search')} help={<HelpTip k="support.search" />}>
            {(p) => (
              <Input
                {...p}
                type="search"
                value={q}
                maxLength={100}
                placeholder={t('admin.support.searchPlaceholder')}
                onChange={(e) => {
                  setQ(e.target.value);
                  reset();
                }}
              />
            )}
          </Field>
        </div>
        {!d ? (
          <Spinner label={t('common.loading')} />
        ) : !d.items.length ? (
          <EmptyState
            title={t('admin.support.emptyTitle')}
            description={
              status === 'active' && !assigned && !priority && !q ? t('admin.support.emptyLead') : undefined
            }
          />
        ) : (
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full min-w-[48rem] text-sm" aria-label={t('admin.support.listTitle')}>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.support.colTicket')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.support.colCustomer')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.support.colStatus')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.support.colAssignee')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.support.colLast')}</th>
                </tr>
              </thead>
              <tbody>
                {d.items.map((x) => (
                  <tr key={x.id} className="border-t border-line align-top">
                    <td className="px-3 py-2">
                      <Link
                        to={`${ROUTE}/$id`}
                        params={{ id: x.id }}
                        className="font-medium text-pine underline underline-offset-2 hover:no-underline"
                      >
                        #{x.number} {x.subject}
                      </Link>
                      <span className="block text-ink-3">{t(`admin.support.categories.${x.category}`)}</span>
                    </td>
                    <td className="px-3 py-2">
                      {x.name}
                      <span className="block text-ink-3">{x.email}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="flex flex-wrap gap-1">
                        <Badge tone={statusTone[x.status]}>{t(`admin.support.statuses.${x.status}`)}</Badge>
                        {x.priority !== 'normal' && (
                          <Badge tone={priorityTone[x.priority]}>
                            {t(`admin.support.priorities.${x.priority}`)}
                          </Badge>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-ink-2">
                      {x.assigneeName ?? t('admin.support.unassigned')}
                    </td>
                    <td className="px-3 py-2 text-end text-ink-2">{formatDate(x.lastMessageAt, true)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {d && pages > 1 && (
          <div className="mt-4 flex items-center gap-3 text-sm">
            <Button size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              {t('common.previous')}
            </Button>
            <span className="text-ink-2">{t('admin.support.pageOf', { page, pages })}</span>
            <Button size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
              {t('common.next')}
            </Button>
          </div>
        )}
      </Section>
      <ReportSection />
      <MacrosSection />
    </PageFrame>
  );
}

// ---------------------------------------------------------------------------- Rapor

function ReportSection() {
  const t = useT();
  const { locale } = useI18n();
  const duration = useDuration();
  const [days, setDays] = useState(30);
  const q = useQuery({
    queryKey: ['support-report', days],
    queryFn: () => api.get<SupportReport>(`/v1/admin/support/report?days=${days}`),
  });
  const r = q.data;
  const avg = (tenths: number | null) =>
    tenths === null ? '—' : new Intl.NumberFormat(locale === 'tr' ? 'tr-TR' : 'en-GB').format(tenths / 10);
  const maxDist = r ? Math.max(1, ...r.satisfaction.distribution) : 1;
  return (
    <Section
      title={t('admin.support.report')}
      description={t('admin.support.reportLead')}
      action={
        <div className="flex items-end gap-1">
          <Field label={t('admin.support.period')} className="w-40">
            {(p) => (
              <Select {...p} value={String(days)} onChange={(e) => setDays(Number(e.target.value))}>
                {[7, 30, 90, 365].map((n) => (
                  <option key={n} value={n}>
                    {t('admin.support.lastDays', { days: n })}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <span className="pb-2">
            <HelpTip k="support.report_period" />
          </span>
        </div>
      }
    >
      {!r ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div className="space-y-6">
          <dl className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5" aria-label={t('admin.support.report')}>
            {[
              { label: t('admin.support.statCreated'), value: r.tickets.created },
              { label: t('admin.support.statResolved'), value: r.tickets.resolved },
              { label: t('admin.support.statOpen'), value: r.tickets.open + r.tickets.waiting },
              { label: t('admin.support.statFirstResponse'), value: duration(r.firstResponse.medianMinutes) },
              {
                label: t('admin.support.statSatisfaction'),
                value: r.satisfaction.count
                  ? t('admin.support.satisfactionValue', {
                      avg: avg(r.satisfaction.averageTenths),
                      count: r.satisfaction.count,
                    })
                  : '—',
              },
            ].map((x) => (
              <div key={x.label} className="rounded-md border border-line bg-surface px-3 py-2">
                <dt className="text-xs text-ink-3">{x.label}</dt>
                <dd className="mt-0.5 text-[0.9375rem] font-medium tabular">{x.value}</dd>
              </div>
            ))}
          </dl>
          <div className="grid gap-6 lg:grid-cols-2">
            <table className="w-full text-sm" aria-label={t('admin.support.distribution')}>
              <caption className="mb-2 text-start font-medium">{t('admin.support.distribution')}</caption>
              <tbody>
                {[5, 4, 3, 2, 1].map((score) => {
                  const n = r.satisfaction.distribution[score - 1] ?? 0;
                  return (
                    <tr key={score}>
                      <th scope="row" className="w-16 py-1 text-start font-normal text-ink-2">
                        {t('admin.support.score', { score })}
                      </th>
                      <td className="py-1">
                        <span
                          aria-hidden
                          className="block h-2.5 rounded-sm bg-pine"
                          style={{ width: `${Math.round((n / maxDist) * 100)}%`, minWidth: n ? '0.5rem' : 0 }}
                        />
                      </td>
                      <td className="w-12 py-1 text-end tabular">{n}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <table className="w-full text-sm" aria-label={t('admin.support.byCategory')}>
              <caption className="mb-2 text-start font-medium">{t('admin.support.byCategory')}</caption>
              <tbody>
                {r.byCategory.length ? (
                  r.byCategory.map((c) => (
                    <tr key={c.category} className="border-t border-line">
                      <th scope="row" className="py-1.5 text-start font-normal">
                        {t(`admin.support.categories.${c.category}`)}
                      </th>
                      <td className="py-1.5 text-end tabular">{c.count}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="py-1.5 text-ink-3">{t('admin.support.noData')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {r.returnReasons && (
            <div className="grid gap-6 lg:grid-cols-2">
              <table className="w-full text-sm" aria-label={t('admin.support.returnReasons')}>
                <caption className="mb-2 text-start font-medium">
                  {t('admin.support.returnReasons')}{' '}
                  <span className="font-normal text-ink-3">
                    ({t('admin.support.returnTotal', { count: r.returnReasons.total })})
                  </span>
                </caption>
                <thead className="text-ink-3">
                  <tr>
                    <th className="py-1 text-start font-normal">{t('admin.support.colReason')}</th>
                    <th className="py-1 text-end font-normal">{t('admin.support.colRequests')}</th>
                    <th className="py-1 text-end font-normal">{t('admin.support.colApproved')}</th>
                  </tr>
                </thead>
                <tbody>
                  {r.returnReasons.byKind.length ? (
                    r.returnReasons.byKind.map((k) => (
                      <tr key={k.kind} className="border-t border-line">
                        <th scope="row" className="py-1.5 text-start font-normal">
                          {t(`admin.returns.kinds.${k.kind}`)}
                        </th>
                        <td className="py-1.5 text-end tabular">{k.count}</td>
                        <td className="py-1.5 text-end tabular">{k.approved}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="py-1.5 text-ink-3">
                        {t('admin.support.noData')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <table className="w-full text-sm" aria-label={t('admin.support.topReturned')}>
                <caption className="mb-2 text-start font-medium">{t('admin.support.topReturned')}</caption>
                <tbody>
                  {r.returnReasons.topProducts.length ? (
                    r.returnReasons.topProducts.map((x) => (
                      <tr key={x.title} className="border-t border-line">
                        <th scope="row" className="py-1.5 text-start font-normal">
                          {x.title}
                        </th>
                        <td className="py-1.5 text-end tabular">
                          {t('admin.support.units', { count: x.quantity })}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="py-1.5 text-ink-3">{t('admin.support.noData')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------- Hazır yanıtlar

function MacrosSection() {
  const t = useT();
  const { can } = usePermissions();
  const manage = can('customers.ticket.manage');
  const [editing, setEditing] = useState<SupportMacro | 'new' | null>(null);
  const q = useQuery({
    queryKey: ['support-macros'],
    queryFn: () => api.get<SupportMacro[]>('/v1/admin/support/macros'),
  });
  return (
    <Section
      title={t('admin.support.macros')}
      description={t('admin.support.macrosLead')}
      action={
        manage && (
          <span className="inline-flex items-center gap-1">
            <Button size="sm" onClick={() => setEditing('new')}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.support.macroAdd')}
            </Button>
            <HelpTip k="support.macro_add" />
          </span>
        )
      }
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !q.data.length ? (
        <EmptyState title={t('admin.support.macrosEmpty')} description={t('admin.support.macrosEmptyLead')} />
      ) : (
        <ul
          className="divide-y divide-line rounded-md border border-line"
          aria-label={t('admin.support.macros')}
        >
          {q.data.map((m) => (
            <MacroRow key={m.id} m={m} manage={manage} onEdit={() => setEditing(m)} />
          ))}
        </ul>
      )}
      {editing && <MacroDialog macro={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </Section>
  );
}

function MacroRow({ m, manage, onEdit }: { m: SupportMacro; manage: boolean; onEdit: () => void }) {
  const t = useT();
  const remove = useApiMutation(() => api.del(`/v1/admin/support/macros/${m.id}`), {
    invalidate: [['support-macros']],
    success: t('admin.support.macroDeleted'),
  });
  return (
    <li className="flex flex-wrap items-start justify-between gap-3 px-4 py-3 text-sm">
      <div className="min-w-0 max-w-[60ch]">
        <p className="font-medium">{m.title}</p>
        <p className="line-clamp-2 whitespace-pre-line text-ink-3">{m.body}</p>
      </div>
      {manage && (
        <span className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={onEdit}
            aria-label={t('admin.support.macroEditNamed', { name: m.title })}
          >
            <Pencil className="h-4 w-4" aria-hidden />
          </Button>
          <HelpTip k="support.macro_edit" />
          <ConfirmButton
            variant="ghost"
            message={t('admin.support.macroDeleteConfirm', { name: m.title })}
            onConfirm={() => remove.mutateAsync(undefined)}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
            <span className="sr-only">{t('admin.support.macroDeleteNamed', { name: m.title })}</span>
          </ConfirmButton>
          <HelpTip k="support.macro_delete" />
        </span>
      )}
    </li>
  );
}

function MacroDialog({ macro, onClose }: { macro: SupportMacro | null; onClose: () => void }) {
  const t = useT();
  const [title, setTitle] = useState(macro?.title ?? '');
  const [body, setBody] = useState(macro?.body ?? '');
  const save = useApiMutation(
    () =>
      macro
        ? api.patch(`/v1/admin/support/macros/${macro.id}`, { title, body, version: macro.version })
        : api.post('/v1/admin/support/macros', { title, body }),
    {
      invalidate: [['support-macros']],
      success: t('admin.support.macroSaved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={macro ? t('admin.support.macroEdit') : t('admin.support.macroAdd')}
      description={t('admin.support.macroDialogLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={title.trim().length < 2 || !body.trim()}
            onClick={() => save.mutate(undefined)}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label={t('admin.support.macroTitle')}
          required
          help={<HelpTip k="support.macro_title" />}
          error={fieldError(save.error, 'title')}
        >
          {(p) => <Input {...p} value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} />}
        </Field>
        <Field
          label={t('admin.support.macroBody')}
          required
          help={<HelpTip k="support.macro_body" />}
          description={t('admin.support.macroBodyHint')}
          error={fieldError(save.error, 'body')}
        >
          {(p) => (
            <Textarea
              {...p}
              className="min-h-32"
              maxLength={5000}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          )}
        </Field>
        {save.error && !save.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {save.error.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------- Ayrıntı

export function SupportTicketPage() {
  const { id } = useParams({ from: '/app/destek-talepleri/$id' });
  const t = useT();
  const q = useQuery({
    queryKey: ['support-ticket', id],
    queryFn: () => api.get<SupportTicket>(`/v1/admin/support/tickets/${id}`),
  });
  if (!q.data) return <Spinner label={t('common.loading')} />;
  return <TicketView key={`${q.data.id}:${q.data.version}`} ticket={q.data} />;
}

function TicketView({ ticket: x }: { ticket: SupportTicket }) {
  const t = useT();
  const { formatDate } = useI18n();
  const duration = useDuration();
  const { can } = usePermissions();
  const manage = can('customers.ticket.manage');
  const agents = useQuery({
    queryKey: ['support-agents'],
    queryFn: () => api.get<SupportAgent[]>('/v1/admin/support/agents'),
  });
  const [status, setStatus] = useState<TicketStatus>(x.status);
  const [priority, setPriority] = useState<Priority>(x.priority);
  const [category, setCategory] = useState<Category>(x.category);
  const [assignedTo, setAssignedTo] = useState(x.assignedTo ?? '');
  const [notify, setNotify] = useState(true);
  const dirty =
    status !== x.status ||
    priority !== x.priority ||
    category !== x.category ||
    assignedTo !== (x.assignedTo ?? '');
  const resolving = status === 'resolved' && x.status !== 'resolved';
  const invalidate = [['support-ticket', x.id], ['support-tickets'], ['support-report']];
  const save = useApiMutation(
    () =>
      api.patch<SupportTicketUpdated>(`/v1/admin/support/tickets/${x.id}`, {
        status,
        priority,
        category,
        assignedTo: assignedTo || null,
        notify: resolving && notify,
        version: x.version,
      }),
    {
      invalidate,
      success: (r) =>
        resolving && notify
          ? r.emailed
            ? t('admin.support.savedEmailed')
            : t('admin.support.savedNotEmailed')
          : t('common.saved'),
      silentFieldErrors: true,
    },
  );
  const firstResponseMinutes = x.firstResponseAt
    ? Math.round((new Date(x.firstResponseAt).getTime() - new Date(x.createdAt).getTime()) / 60_000)
    : null;
  return (
    <PageFrame
      helpKey="page.support_ticket"
      title={t('admin.support.ticketTitle', { number: x.number, subject: x.subject })}
      actions={
        <Link to={ROUTE} className={buttonStyles({ variant: 'secondary' })}>
          {t('admin.support.backToList')}
        </Link>
      }
    >
      <Section
        title={t('admin.support.details')}
        action={<Badge tone={statusTone[x.status]}>{t(`admin.support.statuses.${x.status}`)}</Badge>}
      >
        <dl className="mb-5 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[minmax(9rem,auto)_1fr]">
          <dt className="text-ink-3">{t('admin.support.customer')}</dt>
          <dd>
            {x.customerId ? (
              <Link
                to="/musteriler/$id"
                params={{ id: x.customerId }}
                className="text-pine underline underline-offset-2 hover:no-underline"
              >
                {x.name}
              </Link>
            ) : (
              <>
                {x.name} <span className="text-ink-3">({t('admin.support.guest')})</span>
              </>
            )}
            <span className="block text-ink-3">{x.email}</span>
          </dd>
          {x.order && (
            <>
              <dt className="text-ink-3">{t('admin.support.order')}</dt>
              <dd>
                <Link
                  to="/siparisler/$id"
                  params={{ id: x.order.id }}
                  className="text-pine underline underline-offset-2 hover:no-underline"
                >
                  {x.order.number}
                </Link>
              </dd>
            </>
          )}
          <dt className="text-ink-3">{t('admin.support.channel')}</dt>
          <dd>{t(`admin.support.channels.${x.channel}`)}</dd>
          <dt className="text-ink-3">{t('admin.support.opened')}</dt>
          <dd>{formatDate(x.createdAt, true)}</dd>
          <dt className="text-ink-3">{t('admin.support.firstResponse')}</dt>
          <dd>{x.firstResponseAt ? duration(firstResponseMinutes) : t('admin.support.noResponseYet')}</dd>
          {x.satisfaction && (
            <>
              <dt className="text-ink-3">{t('admin.support.satisfaction')}</dt>
              <dd data-testid="ticket-satisfaction">
                {t('admin.support.ratingValue', { rating: x.satisfaction.rating })}
                {x.satisfaction.comment && (
                  <span className="block text-ink-2">“{x.satisfaction.comment}”</span>
                )}
              </dd>
            </>
          )}
        </dl>
        <fieldset disabled={!manage} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t('admin.support.status')} help={<HelpTip k="support.status" />}>
            {(p) => (
              <Select {...p} value={status} onChange={(e) => setStatus(e.target.value as TicketStatus)}>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`admin.support.statuses.${s}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.support.priority')} help={<HelpTip k="support.priority" />}>
            {(p) => (
              <Select {...p} value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
                {PRIORITIES.map((s) => (
                  <option key={s} value={s}>
                    {t(`admin.support.priorities.${s}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.support.category')} help={<HelpTip k="support.category" />}>
            {(p) => (
              <Select {...p} value={category} onChange={(e) => setCategory(e.target.value as Category)}>
                {CATEGORIES.map((s) => (
                  <option key={s} value={s}>
                    {t(`admin.support.categories.${s}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label={t('admin.support.assignee')}
            help={<HelpTip k="support.assignee" />}
            error={fieldError(save.error, 'assignedTo')}
          >
            {(p) => (
              <Select {...p} value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
                <option value="">{t('admin.support.unassigned')}</option>
                {(agents.data ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
                {x.assignedTo && !agents.data?.some((a) => a.id === x.assignedTo) && (
                  <option value={x.assignedTo}>{x.assigneeName ?? '—'}</option>
                )}
              </Select>
            )}
          </Field>
        </fieldset>
        {manage && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {resolving && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--zt-pine)]"
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                />
                {t('admin.support.notifyResolved')}
                <HelpTip k="support.notify_resolved" />
              </label>
            )}
            <span className="inline-flex items-center gap-1">
              <Button
                variant="primary"
                disabled={!dirty}
                loading={save.isPending}
                onClick={() => save.mutate(undefined)}
              >
                {t('admin.support.saveDetails')}
              </Button>
              <HelpTip k="support.save_details" />
            </span>
            {save.error && !save.error.errors.length && (
              <p role="alert" className="text-sm text-rose">
                {save.error.title}
              </p>
            )}
          </div>
        )}
      </Section>

      <Section title={t('admin.support.conversation')}>
        <ol className="space-y-3" aria-label={t('admin.support.conversation')}>
          {x.messages.map((m) => (
            <li
              key={m.id}
              className={
                m.kind === 'note'
                  ? 'rounded-md border border-dashed border-amber/50 bg-amber-tint px-4 py-3 text-sm'
                  : m.kind === 'staff'
                    ? 'ms-6 rounded-md border border-line bg-surface-2 px-4 py-3 text-sm'
                    : 'me-6 rounded-md border border-line bg-surface px-4 py-3 text-sm'
              }
            >
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <Badge tone={m.kind === 'note' ? 'amber' : m.kind === 'staff' ? 'pine' : 'neutral'}>
                  {t(`admin.support.kinds.${m.kind}`)}
                </Badge>
                <span className="font-medium">{m.authorName ?? '—'}</span>
                <time dateTime={m.createdAt} className="text-ink-3">
                  {formatDate(m.createdAt, true)}
                </time>
                {m.kind === 'staff' && (
                  <span className="text-ink-3">
                    {m.emailedAt ? t('admin.support.emailed') : t('admin.support.notEmailed')}
                  </span>
                )}
              </div>
              <p className="whitespace-pre-line break-words">{m.body}</p>
            </li>
          ))}
        </ol>
        {manage && <Composer ticket={x} />}
      </Section>
    </PageFrame>
  );
}

function Composer({ ticket: x }: { ticket: SupportTicket }) {
  const t = useT();
  const [text, setText] = useState('');
  const [idem, setIdem] = useState(() => new IdempotencyKey());
  const macros = useQuery({
    queryKey: ['support-macros'],
    queryFn: () => api.get<SupportMacro[]>('/v1/admin/support/macros'),
  });
  const closed = x.status === 'closed';
  const send = useApiMutation(
    (v: { kind: 'staff' | 'note'; resolve: boolean }) =>
      api.post<SupportTicketReply>(
        `/v1/admin/support/tickets/${x.id}/messages`,
        { kind: v.kind, body: text, resolve: v.resolve },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['support-ticket', x.id], ['support-tickets'], ['support-report']],
      success: (r) =>
        r.messages[r.messages.length - 1]?.kind === 'note'
          ? t('admin.support.noteSaved')
          : r.emailed
            ? t('admin.support.replySent')
            : t('admin.support.replySavedNotEmailed'),
      onSuccess: () => {
        setText('');
        setIdem(new IdempotencyKey());
      },
      silentFieldErrors: true,
    },
  );
  const insertMacro = (id: string) => {
    const m = macros.data?.find((y) => y.id === id);
    if (!m) return;
    const body = m.body.replace(/\{\{\s*name\s*\}\}/g, x.name);
    setText((cur) => (cur.trim() ? `${cur.trimEnd()}\n\n${body}` : body));
  };
  const ready = text.trim().length > 0;
  return (
    <div className="mt-5 space-y-3 border-t border-line pt-4">
      {closed && <p className="text-sm text-ink-2">{t('admin.support.closedHint')}</p>}
      {!!macros.data?.length && !closed && (
        <Field
          label={t('admin.support.insertMacro')}
          help={<HelpTip k="support.insert_macro" />}
          className="max-w-sm"
        >
          {(p) => (
            <Select
              {...p}
              value=""
              onChange={(e) => {
                insertMacro(e.target.value);
              }}
            >
              <option value="">{t('admin.support.chooseMacro')}</option>
              {macros.data.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.title}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
      {!closed && (
        <SupportReplyDraft
          ticketId={x.id}
          onDraft={(reply) => setText((cur) => (cur.trim() ? `${cur.trimEnd()}\n\n${reply}` : reply))}
        />
      )}
      <Field
        label={closed ? t('admin.support.noteLabel') : t('admin.support.replyLabel')}
        help={<HelpTip k="support.reply_body" />}
        description={closed ? undefined : t('admin.support.replyHint')}
        error={fieldError(send.error, 'body')}
      >
        {(p) => (
          <Textarea
            {...p}
            className="min-h-32"
            maxLength={5000}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        )}
      </Field>
      <div className="flex flex-wrap items-center gap-2">
        {!closed && (
          <>
            <Button
              variant="primary"
              disabled={!ready}
              loading={send.isPending && send.variables?.kind === 'staff' && !send.variables.resolve}
              onClick={() => send.mutate({ kind: 'staff', resolve: false })}
            >
              {t('admin.support.sendReply')}
            </Button>
            <HelpTip k="support.send_reply" />
            <Button
              disabled={!ready}
              loading={send.isPending && !!send.variables?.resolve}
              onClick={() => send.mutate({ kind: 'staff', resolve: true })}
            >
              {t('admin.support.sendAndResolve')}
            </Button>
            <HelpTip k="support.send_resolve" />
          </>
        )}
        <Button
          variant="ghost"
          disabled={!ready}
          loading={send.isPending && send.variables?.kind === 'note'}
          onClick={() => send.mutate({ kind: 'note', resolve: false })}
        >
          {t('admin.support.addNote')}
        </Button>
        <HelpTip k="support.add_note" />
      </div>
      {send.error && !send.error.errors.length && (
        <p role="alert" className="text-sm text-rose">
          {send.error.title}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------- İletişim mesajından talep

type Submission = FormSubmissionList['items'][number];

/** İletişim mesajını destek talebine dönüştürür; başarılıysa talebin sayfasına gider. */
export function ConvertTicketDialog({
  submission,
  onClose,
}: {
  submission: Submission;
  onClose: () => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const message = String(submission.answers.find((a) => a.preset === 'message')?.value ?? '');
  const [subject, setSubject] = useState(message.replace(/\s+/g, ' ').trim().slice(0, 80));
  const [category, setCategory] = useState<Category>('other');
  const convert = useApiMutation(
    () =>
      api.post<{ id: string; number: number }>(`/v1/admin/form-submissions/${submission.id}/ticket`, {
        subject: subject.trim() || undefined,
        category,
      }),
    {
      invalidate: [['form-submissions'], ['form-definition'], ['support-tickets']],
      success: (r) => t('admin.support.converted', { number: r.number }),
      onSuccess: (r) => {
        onClose();
        void navigate({ to: `${ROUTE}/$id`, params: { id: r.id } });
      },
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.support.convertTitle')}
      description={t('admin.support.convertLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={convert.isPending}
            disabled={subject.trim().length < 3}
            onClick={() => convert.mutate(undefined)}
          >
            {t('admin.support.convert')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label={t('admin.support.subject')}
          required
          help={<HelpTip k="support.convert_subject" />}
          error={fieldError(convert.error, 'subject')}
        >
          {(p) => (
            <Input {...p} value={subject} maxLength={160} onChange={(e) => setSubject(e.target.value)} />
          )}
        </Field>
        <Field label={t('admin.support.category')} help={<HelpTip k="support.category" />}>
          {(p) => (
            <Select {...p} value={category} onChange={(e) => setCategory(e.target.value as Category)}>
              {CATEGORIES.map((s) => (
                <option key={s} value={s}>
                  {t(`admin.support.categories.${s}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {convert.error && !convert.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {convert.error.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}
